import { useState } from "react"
import { Button } from "@holigral/shared/ui/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@holigral/shared/ui/components/ui/card"
import { Spinner } from "@holigral/shared/ui/components/ui/spinner"
import { topUpAction } from "./actions"
import { useGetBillingSummaryQuery } from "./api"
import CreditBalanceBadge from "./CreditBalanceBadge"
import PaymentMethodCard from "./PaymentMethodCard"
import PurchaseHistory from "./PurchaseHistory"
import TopUp from "./TopUp"
import { useT } from "@/i18n"
import { useCurrentTenant } from "@/tenant"

/**
 * The workspace Billing page (product.md §10.5), Owner-only. Balance, a
 * standalone top-up, in-app card replacement, and purchase history.
 */
export default function BillingPage() {
  const { t } = useT("money")
  const { tenant, isLoading } = useCurrentTenant()
  const isOwner = tenant?.permissions.billingAdmin ?? false
  const { data: summary } = useGetBillingSummaryQuery(undefined, {
    skip: !isOwner,
  })
  const [topUpOpen, setTopUpOpen] = useState(false)

  if (isLoading || !tenant) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }

  if (!isOwner) {
    return (
      <div className="mx-auto w-full max-w-3xl px-6 py-8 text-sm text-muted-foreground">
        {t("billing.notAuthorized")}
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-6 py-8">
      <Card>
        <CardHeader>
          <CardTitle>{t("billing.title")}</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                {t("balance.label")}
              </span>
              {summary && <CreditBalanceBadge balance={summary.balance} />}
            </span>
            {!!summary && summary.granted > 0 && (
              <span className="text-xs text-muted-foreground">
                {t("balance.breakdown", {
                  purchased: summary.purchased,
                  granted: summary.granted,
                })}
              </span>
            )}
          </div>
          <Button
            onClick={() => {
              setTopUpOpen(true)
            }}
          >
            {t("topUp.cta")}
          </Button>
        </CardContent>
      </Card>

      <PaymentMethodCard card={summary?.defaultPaymentMethod ?? null} />

      <PurchaseHistory />

      <TopUp
        action={topUpAction()}
        shortfall={0}
        presentation="dialog"
        open={topUpOpen}
        onOpenChange={setTopUpOpen}
      />
    </div>
  )
}
